import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { moveFocus } from "./menuNav";
import { placeMenu, placeSubmenu, type MenuAnchor } from "./menuPlacement";
import { isSelectable, type MenuItemSpec } from "./menuSpec";

/**
 * The one menu in the app (日本-318) — the `⋯` and the right-click alike.
 *
 * Replaces three independent re-implementations of one visual shape: `SentenceMenu`'s whole file,
 * `Library`'s file-private `Menu`/`MenuItem`, and `TabStrip`'s overflow popup. They disagreed on
 * click-outside (one deferred a frame, one did not), on Escape (the overflow had none at all), on
 * `stopPropagation`, on alignment and on z-index — so a fourth would have been a fourth copy, and
 * a right-click on each would have been three more.
 *
 * ## Why one owner rather than a component each surface renders
 *
 * A right-click opens at arbitrary pointer coordinates, which `absolute`-inside-`relative` cannot
 * express. Everything therefore renders from here, `position: fixed`, placed by `menuPlacement`.
 *
 * That is not only what enables the feature — it removes two latent bugs the old menus each had.
 * All three were `absolute` inside a scroll container and so **clippable**, which is invisible to a
 * role query because a clipped menu is still present (`TagChips.tsx` had to solve this once
 * already). And an unfocused sentence row carries `opacity: .58`, which creates a stacking context,
 * so its own menu could paint *under* a later row.
 *
 * `fixed` is viewport-positioned only while no ancestor carries `transform`, `filter`, `contain` or
 * `perspective`. None does today, anywhere in `components/shell/` or the surfaces. If one ever
 * appears, wrap the returned element in `createPortal` — the placement functions do not change.
 *
 * ## Not an OverlayId, and that is deliberate
 *
 * `navHistory.ts` rules this out by name: a menu has an implicit teardown and is not worth a press
 * of Back. But it must not survive one either, so this closes on `popstate` without ever writing
 * an entry — along with scroll, resize and blur, all of which leave a `fixed` menu stranded beside
 * something that has moved.
 */

export interface MenuApi {
  /** Right-click: anchored to a point in the viewport. */
  openAt(point: { x: number; y: number }, items: MenuItemSpec[], key?: string): void;
  /** A `⋯` button: anchored to its rect, and toggling on the same trigger. */
  openFrom(
    trigger: HTMLElement,
    items: MenuItemSpec[],
    key: string,
    align?: "start" | "end",
  ): void;
  close(): void;
  /** Shut whatever is open, for a caller about to take the screen — see `Dialogs`. */
  closeAll(): void;
  /** So a `⋯` can render `aria-expanded` and stay visible while its menu is open. */
  openKey: string | null;
}

interface OpenMenu {
  key: string | null;
  items: MenuItemSpec[];
  anchor: MenuAnchor;
  /** Restored when the menu closes, so a `⋯` keeps focus after Escape. */
  trigger: HTMLElement | null;
}

const Context = createContext<MenuApi | null>(null);

export function useMenu(): MenuApi {
  const api = useContext(Context);
  if (!api) throw new Error("useMenu needs a MenuProvider above it");
  return api;
}

export function MenuProvider({
  isPhone = false,
  children,
}: {
  /** Submenus render as an inline band rather than a flyout where there is no hover. */
  isPhone?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<OpenMenu | null>(null);
  const openRef = useRef<OpenMenu | null>(null);
  openRef.current = open;

  const close = useCallback(() => {
    const trigger = openRef.current?.trigger ?? null;
    openRef.current = null;
    setOpen(null);
    trigger?.focus?.();
  }, []);

  const api = useMemo<MenuApi>(
    () => ({
      openAt: (point, items, key) => {
        setOpen({ key: key ?? null, items, anchor: { x: point.x, y: point.y }, trigger: null });
      },
      openFrom: (trigger, items, key, align = "start") => {
        // Toggling on the same trigger, which is what all three old menus did.
        if (openRef.current?.key === key) {
          close();
          return;
        }
        const rect = trigger.getBoundingClientRect();
        setOpen({
          key,
          items,
          // Hung below the trigger, and flipping about its FAR edge so a flipped menu never lands
          // on top of the button that opened it.
          anchor:
            align === "end"
              ? { x: rect.right, y: rect.bottom, flipX: rect.right, flipY: rect.top }
              : { x: rect.left, y: rect.bottom, flipX: rect.right, flipY: rect.top },
          trigger,
        });
      },
      close,
      closeAll: close,
      openKey: open?.key ?? null,
    }),
    [close, open],
  );

  /**
   * Escape, in the capture phase and registered at mount.
   *
   * Bubble-phase would lose to `Library`'s own ladder, which is registered earlier and knows
   * nothing about a provider-owned menu — so Escape aimed at a menu would *leave the library*.
   * Capture plus `stopImmediatePropagation` sidesteps registration order entirely.
   *
   * Registered at mount rather than on open for `Dialogs.tsx`'s reason: capture listeners fire
   * earliest-first, so mounting inside `App` is what puts this ahead of the lightbox's. `Dialogs`
   * is nested *inside* this provider and so registers even earlier, which is the order wanted — a
   * dialog opened from a menu owns Escape.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!openRef.current) return;
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [close]);

  /**
   * Everything that leaves a `fixed` menu stranded beside something that has moved.
   *
   * `popstate` is the one that matters for correctness rather than tidiness: this writes no history
   * entry on purpose, so without closing here a Back would navigate the app out from under an open
   * menu and leave it floating over the new surface.
   */
  useEffect(() => {
    const shut = () => {
      if (openRef.current) close();
    };
    window.addEventListener("popstate", shut);
    window.addEventListener("resize", shut);
    window.addEventListener("blur", shut);
    // Capture, so a scroll inside any region reaches this — scroll does not bubble to `window`
    // from an element. Passive: nothing here calls `preventDefault`.
    window.addEventListener("scroll", shut, { capture: true, passive: true });
    return () => {
      window.removeEventListener("popstate", shut);
      window.removeEventListener("resize", shut);
      window.removeEventListener("blur", shut);
      window.removeEventListener("scroll", shut, { capture: true });
    };
  }, [close]);

  return (
    <Context.Provider value={api}>
      {children}
      {open && (
        <MenuSurface key={itemsKey(open)} open={open} isPhone={isPhone} onClose={close} />
      )}
    </Context.Provider>
  );
}

/** A new anchor or a new item list is a new menu, so focus and any open submenu start over. */
function itemsKey(open: OpenMenu): string {
  return `${open.key ?? "point"}:${open.anchor.x}:${open.anchor.y}`;
}

function MenuSurface({
  open,
  isPhone,
  onClose,
}: {
  open: OpenMenu;
  isPhone: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null);
  const [focused, setFocused] = useState<number | null>(null);
  const [submenu, setSubmenu] = useState<string | null>(null);

  // Measure, then place. Two passes, but the first is never painted — `visibility: hidden` until
  // `placed` is set, because a menu that appears at the pointer and then jumps is worse than one
  // that appears a frame later.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    setPlaced(
      placeMenu(open.anchor, { width, height }, {
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    );
  }, [open]);

  /**
   * Take focus, once placed — which is what makes `onKeyDown` below fire at all.
   *
   * **`autoFocus` on a `<div>` does nothing**, and it looks like it should: react-dom honours the
   * prop only in `commitMount` for `button`, `input`, `select` and `textarea`, and `setProp` skips
   * writing the attribute for every other tag. So the menu never had focus, React's `onKeyDown`
   * never fired, and every arrow key, Enter and Home/End in `menuNav.ts` was unreachable — while
   * Escape still worked, because that one lives on `window` in the provider. Exactly the shape of
   * bug nobody reports: the menu opens, it closes, and only the keyboard is dead.
   *
   * After placement rather than at mount, because the first pass renders `visibility: hidden` and a
   * hidden element is not focusable.
   */
  useLayoutEffect(() => {
    if (placed) ref.current?.focus();
  }, [placed]);

  // Click-outside. `mousedown` rather than `click`, matching what the three old menus did, and
  // deferred a frame for `Library`'s reason: the click that OPENED this is still propagating, and
  // closing on it would make every trigger a no-op.
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const handle = window.setTimeout(() => {
      window.addEventListener("mousedown", onDown);
      // A right-click elsewhere must close this too, and it fires no `mousedown` on every platform.
      window.addEventListener("contextmenu", onDown);
    }, 0);
    return () => {
      window.clearTimeout(handle);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("contextmenu", onDown);
    };
  }, [onClose]);

  const choose = useCallback(
    (item: Extract<MenuItemSpec, { kind: "item" }>) => {
      onClose();
      item.onSelect();
    },
    [onClose],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Escape is the provider's, at the window, in the capture phase — it never reaches here.
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setFocused(moveFocus(open.items, focused, event.key));
      setSubmenu(null);
      return;
    }
    const current = focused === null ? undefined : open.items[focused];
    if (event.key === "ArrowRight" && current?.kind === "submenu") {
      event.preventDefault();
      setSubmenu(current.id);
      return;
    }
    if (event.key === "ArrowLeft" && submenu !== null) {
      event.preventDefault();
      setSubmenu(null);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      if (current?.kind === "item") {
        event.preventDefault();
        choose(current);
      } else if (current?.kind === "submenu") {
        event.preventDefault();
        setSubmenu(current.id);
      }
    }
  };

  return (
    <div
      ref={ref}
      role="menu"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      // The menu renders outside every row now, so nothing of its own can reach a `role="button"`
      // ancestor — but a stray drag starting on it would still be a tab or row drag, so refuse.
      draggable={false}
      className="fixed min-w-[190px] max-w-[280px] rounded-[var(--r-2)] border border-line bg-panel py-1 text-left"
      style={{
        zIndex: "var(--z-menu)",
        boxShadow: "var(--sh-popover)",
        left: placed?.left ?? open.anchor.x,
        top: placed?.top ?? open.anchor.y,
        visibility: placed ? "visible" : "hidden",
      }}
    >
      {open.items.map((item, index) => (
        <Row
          key={item.id}
          item={item}
          focused={focused === index}
          submenuOpen={item.kind === "submenu" && submenu === item.id}
          isPhone={isPhone}
          onFocus={() => setFocused(index)}
          onOpenSubmenu={() => setSubmenu(item.kind === "submenu" ? item.id : null)}
          onChoose={choose}
        />
      ))}
    </div>
  );
}

function Row({
  item,
  focused,
  submenuOpen,
  isPhone,
  onFocus,
  onOpenSubmenu,
  onChoose,
}: {
  item: MenuItemSpec;
  focused: boolean;
  submenuOpen: boolean;
  isPhone: boolean;
  onFocus: () => void;
  onOpenSubmenu: () => void;
  onChoose: (item: Extract<MenuItemSpec, { kind: "item" }>) => void;
}) {
  const parentRef = useRef<HTMLButtonElement>(null);

  if (item.kind === "separator") {
    return <div role="separator" className="my-1 border-t border-line-soft" />;
  }

  if (item.kind === "caption") {
    return (
      <span className="block px-3 pb-1 font-mono text-[14px] uppercase tracking-[.1em] text-ink-4">
        {item.label}
      </span>
    );
  }

  if (item.kind === "note") {
    return <span className="block px-3 py-[4px] text-[14px]/[1.4] text-ink-4">{item.label}</span>;
  }

  if (item.kind === "swatches") {
    // 26px on a pointer, 44px and sharing the width on a phone — the frames' own numbers, and the
    // phone figure is the minimum touch target the handoff sets rather than a coincidence.
    const size = isPhone ? undefined : 26;
    return (
      <div className={isPhone ? "flex gap-2.5 px-3 pb-3" : "flex gap-1.5 px-3 pb-2"}>
        {item.swatches.map((swatch) => (
          <button
            key={swatch.id}
            type="button"
            role="menuitemradio"
            aria-checked={swatch.selected}
            aria-label={swatch.label}
            onClick={swatch.onSelect}
            className={isPhone ? "min-h-[44px] flex-1 rounded-[var(--r-3)]" : "rounded-[var(--r-2)]"}
            style={{
              background: swatch.color,
              width: size,
              height: size,
              // The selected ring is drawn OUTSIDE the swatch on a white gap, so it reads at 26px
              // against any of the five hues. A border would eat into the colour instead.
              boxShadow: swatch.selected
                ? "0 0 0 2px var(--panel), 0 0 0 3.5px var(--ink)"
                : undefined,
            }}
          />
        ))}
        {item.clear && (
          <button
            key={item.clear.id}
            type="button"
            role="menuitemradio"
            aria-checked={item.clear.selected}
            aria-label={item.clear.label}
            onClick={item.clear.onSelect}
            className={[
              "flex items-center justify-center border border-line-strong bg-void text-ink-3",
              isPhone ? "min-h-[44px] flex-1 rounded-[var(--r-3)] text-[17px]" : "rounded-[var(--r-2)] text-[15px]",
            ].join(" ")}
            style={{ width: size, height: size }}
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  if (item.kind === "submenu") {
    return (
      <>
        <button
          ref={parentRef}
          type="button"
          role="menuitem"
          data-check={`menu.${item.id}`}
          aria-haspopup="menu"
          aria-expanded={submenuOpen}
          // **Opens on hover AND on click, and opens rather than toggles.** Hover has already
          // opened it by the time a pointer can click, so a toggle here would close the submenu on
          // the very gesture meant to commit to it — which reads as the item being dead. The rule
          // is `SentenceMenu`'s, kept verbatim; only the rendering changed.
          onMouseEnter={() => {
            onFocus();
            onOpenSubmenu();
          }}
          onClick={onOpenSubmenu}
          className={[
            "flex w-full items-center justify-between gap-2 px-3 py-[5px] text-[14px]/[1.5] text-ink-2",
            focused || submenuOpen ? "bg-[color:var(--tab-hover)]" : "hover:bg-[color:var(--tab-hover)]",
          ].join(" ")}
        >
          {item.label}
          <span aria-hidden className="text-ink-4">
            ›
          </span>
        </button>
        {submenuOpen &&
          (isPhone ? (
            // No hover on a phone, so the flyout has no way to be reached. The band is what the
            // three old menus did everywhere, and it is still right here.
            <div className="border-y border-line-soft bg-[color:var(--sunken)] py-1">
              <SubmenuItems items={item.items} empty={item.empty} onChoose={onChoose} />
            </div>
          ) : (
            <Flyout parent={parentRef.current} items={item.items} empty={item.empty} onChoose={onChoose} />
          ))}
      </>
    );
  }

  return (
    <button
      type="button"
      role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
      aria-checked={item.checked}
      data-check={`menu.${item.id}`}
      disabled={item.disabled}
      onMouseEnter={onFocus}
      onClick={() => onChoose(item)}
      className={[
        "flex w-full items-center justify-between gap-2 px-3 py-[5px] text-left text-[14px]/[1.5]",
        item.disabled ? "text-ink-off" : item.danger ? "text-ink-2 hover:text-accent-text" : "text-ink-2",
        focused && !item.disabled ? "bg-[color:var(--tab-hover)]" : "hover:bg-[color:var(--tab-hover)]",
      ].join(" ")}
    >
      <span className="flex min-w-0 flex-col items-start">
        <span className="flex items-center gap-[6px] truncate">
          {item.checked !== undefined && (
            <span aria-hidden className="inline-block w-[14px] shrink-0 font-bold text-accent-text">
              {item.checked ? "✓" : ""}
            </span>
          )}
          {item.swatch && (
            <span
              aria-hidden
              className="inline-block h-[9px] w-[9px] shrink-0 rounded-[var(--r-2)] border border-line"
              style={{ background: item.swatch }}
            />
          )}
          {item.label}
        </span>
        {/* The folder a destination log lives in — two logs called `04` in different works are
            otherwise indistinguishable in this list. */}
        {item.sub && <span className="font-mono text-[14px]/[1.4] text-ink-4">{item.sub}</span>}
      </span>
      {item.hint && <span className="font-mono text-[14px] text-ink-4">{item.hint}</span>}
    </button>
  );
}

/**
 * A submenu, floated beside the item that opened it.
 *
 * This is the one behaviour that deliberately changed rather than being ported. The old inline band
 * pushed everything below it **down, under the pointer** — so opening `Move to log` moved `Delete`
 * to where your hand was already travelling. That was the right trade when the popup was `absolute`
 * in a 38px gutter and a flyout had nowhere to go; `placeSubmenu` gives it somewhere. 日本-318 asks
 * for a "nested hover menu", which is this.
 *
 * It deliberately does **not** close on the parent's `mouseleave`: that is what lets the diagonal
 * travel into the flyout work without a safe-triangle calculation. It closes when another parent is
 * entered, when something is chosen, or when the whole menu closes.
 */
function Flyout({
  parent,
  items,
  empty,
  onChoose,
}: {
  parent: HTMLElement | null;
  items: MenuItemSpec[];
  empty?: string;
  onChoose: (item: Extract<MenuItemSpec, { kind: "item" }>) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !parent) return;
    const parentRect = parent.getBoundingClientRect();
    const { width, height } = element.getBoundingClientRect();
    setPlaced(
      placeSubmenu(
        { left: parentRect.left, right: parentRect.right, top: parentRect.top },
        { width, height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [parent, items]);

  return (
    <div
      ref={ref}
      role="menu"
      className="fixed min-w-[180px] max-w-[280px] rounded-[var(--r-2)] border border-line bg-panel py-1"
      style={{
        zIndex: "var(--z-menu)",
        boxShadow: "var(--sh-popover)",
        left: placed?.left ?? 0,
        top: placed?.top ?? 0,
        visibility: placed ? "visible" : "hidden",
      }}
    >
      <SubmenuItems items={items} empty={empty} onChoose={onChoose} />
    </div>
  );
}

function SubmenuItems({
  items,
  empty,
  onChoose,
}: {
  items: MenuItemSpec[];
  empty?: string;
  onChoose: (item: Extract<MenuItemSpec, { kind: "item" }>) => void;
}) {
  if (items.filter(isSelectable).length === 0) {
    return (
      <span className="block px-3 py-[4px] text-[14px]/[1.4] text-ink-4">
        {empty ?? "ここには何もありません"}
      </span>
    );
  }
  return (
    <>
      {items.map((item) =>
        item.kind === "item" ? (
          <button
            key={item.id}
            type="button"
            role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
            aria-checked={item.checked}
            data-check={`menu.${item.id}`}
            disabled={item.disabled}
            onClick={() => onChoose(item)}
            className={[
              "flex w-full flex-col items-start px-3 py-[4px] text-left hover:bg-[color:var(--tab-hover)]",
              item.disabled ? "text-ink-off" : item.danger ? "hover:text-accent-text" : "",
            ].join(" ")}
          >
            <span className="flex items-center gap-[6px] text-[14px]/[1.4] text-ink-2">
              {/* The toggle column, as the top-level rows draw it — see `MenuItemSpec.checked`. */}
              {item.checked !== undefined && (
                <span aria-hidden className="inline-block w-[14px] shrink-0 font-bold text-accent-text">
                  {item.checked ? "✓" : ""}
                </span>
              )}
              {item.label}
            </span>
            {item.sub && <span className="font-mono text-[14px]/[1.4] text-ink-4">{item.sub}</span>}
          </button>
        ) : item.kind === "caption" ? (
          <span
            key={item.id}
            className="block px-3 pb-1 font-mono text-[14px] uppercase tracking-[.1em] text-ink-4"
          >
            {item.label}
          </span>
        ) : item.kind === "separator" ? (
          <div key={item.id} role="separator" className="my-1 border-t border-line-soft" />
        ) : item.kind === "note" ? (
          <span key={item.id} className="block px-3 py-[4px] text-[14px]/[1.4] text-ink-4">
            {item.label}
          </span>
        ) : null,
      )}
    </>
  );
}
